import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileStack, Search, Calendar, User, Download, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function StatementHistory() {
  const [statements, setStatements] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDriver, setFilterDriver] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchStatements();
    fetchDrivers();
  }, []);

  const fetchStatements = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDriver !== 'all') params.driver_id = filterDriver;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await axios.get(`${API_URL}/statements/history`, { params });
      setStatements(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load statements'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API_URL}/drivers`);
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to load drivers');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const filteredStatements = statements.filter(stmt =>
    stmt.driver_name?.toLowerCase().includes(search.toLowerCase()) ||
    stmt.period?.includes(search)
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="statement-history">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Statement History</h1>
          <p className="text-muted-foreground mt-1">View all generated and uploaded statements</p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by driver name or period..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterDriver} onValueChange={(v) => { setFilterDriver(v); }}>
                <SelectTrigger className="w-48">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Drivers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Drivers</SelectItem>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.driver_name || d.full_name || d.driver_username || d.email || 'Unnamed'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36"
              />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36"
              />
              <Button onClick={fetchStatements}>Apply</Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <span className="text-muted-foreground">
            {filteredStatements.length} statement{filteredStatements.length !== 1 ? 's' : ''}
          </span>
          <span className="text-lg font-bold">
            Total: {formatCurrency(filteredStatements.reduce((sum, s) => sum + (s.total_amount || 0), 0))}
          </span>
        </div>

        {/* Statements Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : filteredStatements.length === 0 ? (
              <div className="text-center py-12">
                <FileStack className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">No statements found</h3>
                <p className="text-muted-foreground mb-4">Generate or upload statements to see them here</p>
                <div className="flex justify-center gap-2">
                  <Button variant="outline" asChild>
                    <a href="/admin/statements/generate">Generate Statement</a>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="/admin/statements">Upload Statement</a>
                  </Button>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Trips</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStatements.map((stmt) => (
                    <TableRow key={stmt.id}>
                      <TableCell className="text-muted-foreground">
                        {stmt.upload_date ? new Date(stmt.upload_date).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="font-medium">{stmt.driver_name || '-'}</TableCell>
                      <TableCell>{stmt.period || '-'}</TableCell>
                      <TableCell>{stmt.total_trips || '-'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {stmt.total_amount ? formatCurrency(stmt.total_amount) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          stmt.status === 'generated' ? 'bg-green-50 text-green-700' :
                          stmt.status === 'uploaded' ? 'bg-blue-50 text-blue-700' :
                          'bg-gray-50'
                        }>
                          {stmt.status || 'uploaded'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {stmt.file_path && (
                            <>
                              <Button variant="ghost" size="sm" asChild>
                                <a href={`${API_URL}/statements/${stmt.id}/preview`} target="_blank">
                                  <Eye className="h-4 w-4" />
                                </a>
                              </Button>
                              <Button variant="ghost" size="sm" asChild>
                                <a href={`${API_URL}/statements/${stmt.id}/download`}>
                                  <Download className="h-4 w-4" />
                                </a>
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
